import type React from "react";
import { useRef, type JSX } from "react";

import classNames from "classnames";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { CircularLoader } from "@similarweb/ui-components";

import type { EnrichmentType } from "app/pages/sales-intelligence/modules/enrichment/types/types";
import { translate } from "app/filters/i18nFilter";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";

import { SL_LOAD_LIST_UPLOADING_TEXT, SL_LOAD_LIST_UPLOAD_TEXT } from "../../../translations/suppressionListKeys";
import type { ErrorTexts, ParsingErrors } from "../../types";
import {
    StyledLoadListUploadContainer,
    StyledInputListWrapper,
    StyledInput,
    StyledLabel,
    LOADER_OPTIONS,
    StyledLabelText,
} from "../style";

import { SupportText } from "./SupportText";

type CSVFileInputProps = {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    isLoading: boolean;
    hasError: boolean;
    parsingErrors: ParsingErrors;
    type: EnrichmentType;
    errorTexts?: ErrorTexts;
    customHint?: string | JSX.Element;
    hintWidth?: number | string;
    hasCustomTracking?: boolean;
    hasHint?: boolean;
};

export const CSVFileInput = ({
    onChange,
    errorTexts,
    isLoading,
    hasError,
    parsingErrors,
    customHint,
    hintWidth,
    hasHint = true,
    hasCustomTracking,
    type,
}: CSVFileInputProps) => {
    const uploadTextKey = isLoading ? SL_LOAD_LIST_UPLOADING_TEXT : SL_LOAD_LIST_UPLOAD_TEXT;
    const fileInputRef = useRef<HTMLInputElement>(null);

    const icon = isLoading ? (
        <CircularLoader options={LOADER_OPTIONS} />
    ) : (
        <SWReactIcons
            iconName="upload-file"
            size="xs"
            iconColor={colorsPalettes.blue[400]}
        />
    );

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event);
        enrichmentTrackingService.csv.trackFileUpload(type);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        if (
            event.dataTransfer.files &&
            event.dataTransfer.files.length > 0 &&
            event.dataTransfer.files[0].type === "text/csv"
        ) {
            const file = event.dataTransfer.files[0];
            const changeEvent = {
                target: { files: [file] },
            } as unknown as React.ChangeEvent<HTMLInputElement>;

            enrichmentTrackingService.csv.trackFileDrop(type);

            onChange(changeEvent);
        }
    };

    return (
        <>
            <StyledLoadListUploadContainer
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                type={type}
            >
                <StyledInputListWrapper className={classNames({ isError: hasError, isFileLoading: isLoading })}>
                    <StyledLabel type={type}>
                        {icon}
                        <StyledLabelText className={classNames({ isFileLoading: isLoading })}>
                            {translate(uploadTextKey)}
                        </StyledLabelText>
                        <StyledInput
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            accept=".csv"
                            type="file"
                        />
                    </StyledLabel>
                </StyledInputListWrapper>
            </StyledLoadListUploadContainer>
            {hasHint || hasError ? (
                <SupportText
                    errorTexts={errorTexts}
                    parsingErrors={parsingErrors}
                    hasError={hasError}
                    customHint={customHint}
                    width={hintWidth}
                    hasCustomTracking={hasCustomTracking}
                    type={type}
                />
            ) : null}
        </>
    );
};
